/**
 * What a selection can be turned into, and what that costs in artifacts.
 *
 * A run is: a selection, a format, a destination. Today only one format is
 * built, so the registry has one entry — but the SHAPE matters now, because the
 * alternative is copy that hardcodes "pages" in six places and has to be
 * unpicked when the second format lands.
 *
 * THE DISTINCTION THE COPY HAS TO CARRY. Some formats produce one artifact PER
 * selected item (148 keywords → 148 pages); others produce ONE artifact from
 * the whole selection (148 keywords → 1 report). That is the single fact a user
 * needs before pressing the button, and it decides everything downstream — a
 * per-item format needs a slug and a link position for each one, a from-all
 * format needs an ordering and a single address.
 *
 * It is never NAMED. No "fan-out", no "fan-in", no "projection", no "create
 * output". The count and the button label carry it: "Make 148 pages" against
 * "Make 1 report" tells you which you are doing without a word of vocabulary.
 *
 * Its own dependency-free module so the label rules are testable — the thing
 * that goes wrong here is a plural, and a plural is invisible in review.
 */

export type RunShape = 'per-item' | 'from-all';

export interface RunFormat {
  key: string;
  /** What the user picks. A concrete verb's object, never a category. */
  label: string;
  blurb: string;
  shape: RunShape;
  /** Singular noun for the artifact produced. Pluralized by the helpers below. */
  artifact: string;
}

/**
 * Registered formats, in the order they are offered.
 *
 * Report, newsletter and live page are deliberately ABSENT rather than present
 * and disabled: an option that cannot be chosen teaches people the controls are
 * decorative. Each is one entry here plus its destination once the backend can
 * make it.
 */
export const RUN_FORMATS: RunFormat[] = [
  {
    key: 'pages',
    label: 'Pages',
    blurb: 'One per keyword, linked to each other',
    shape: 'per-item',
    artifact: 'page',
  },
];

export function formatByKey(key: string): RunFormat | undefined {
  return RUN_FORMATS.find((f) => f.key === key);
}

/** How many artifacts a run over `selectedCount` items produces. */
export function artifactCount(format: RunFormat, selectedCount: number): number {
  return format.shape === 'per-item' ? selectedCount : selectedCount > 0 ? 1 : 0;
}

function plural(noun: string, n: number): string {
  return n === 1 ? noun : `${noun}s`;
}

/** "148 pages" · "1 report" — the count that shows the shape. */
export function runOutcome(format: RunFormat, selectedCount: number): string {
  const n = artifactCount(format, selectedCount);
  return `${n.toLocaleString()} ${plural(format.artifact, n)}`;
}

/** "Make 148 pages" — the button says exactly what pressing it does. */
export function runButtonLabel(format: RunFormat, selectedCount: number): string {
  return `Make ${runOutcome(format, selectedCount)}`;
}

/** "Make something from 148 keywords" — the title says what it is made FROM. */
export function runTitle(selectedCount: number, sourceNoun = 'keyword'): string {
  return `Make something from ${selectedCount.toLocaleString()} ${plural(sourceNoun, selectedCount)}`;
}
